/**
 * Сервис защиты от скриншотов
 * 
 * ВАЖНО: Полная блокировка скриншотов в PWA технически НЕВОЗМОЖНА!
 * 
 * Стратегия:
 * 1. ОБНАРУЖЕНИЕ: Мониторим visibilitychange (срабатывает при скриншоте на iOS/Android)
 * 2. СОКРЫТИЕ: Прячем QR на 200-300ms при подозрении
 * 3. ОБЕСЦЕНИВАНИЕ: QR обновляется каждые 5с + водяные знаки делают скриншот бесполезным
 * 
 * На iOS скриншот = Volume Up/Down + Power Button
 * На Android скриншот = Volume Down + Power Button
 * 
 * При скриншоте часто срабатывает:
 * - visibilitychange (document.hidden = true на ~50-200ms)
 * - blur event (на некоторых устройствах)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

export class ScreenshotProtection {
  private static isEnabled = false
  private static callbacks: Set<() => void> = new Set()
  private static hideTimeout: number | null = null
  private static lastVisibilityChange: number = 0
  private static visibilityHideCount: number = 0
  private static qrHidden: boolean = false
  
  /**
   * Включает защиту от скриншотов
   */
  static enable(): void {
    if (this.isEnabled) return
    
    this.isEnabled = true
    
    // 1. CSS защита от выделения
    document.body.style.userSelect = 'none'
    document.body.style.webkitUserSelect = 'none'
    
    // 2. Блокировка PrintScreen и комбинаций для скриншотов
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('keyup', this.handleKeyup)
    
    // 3. Обнаружение потери фокуса (потенциальный скриншот)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    
    // 4. Блокировка контекстного меню
    document.addEventListener('contextmenu', this.handleContextMenu)
    
    console.log('[ScreenshotProtection] Protection enabled')
  }
  
  /**
   * Отключает защиту
   */
  static disable(): void {
    if (!this.isEnabled) return
    
    this.isEnabled = false
    
    document.body.style.userSelect = ''
    document.body.style.webkitUserSelect = ''
    
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('keyup', this.handleKeyup)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    document.removeEventListener('contextmenu', this.handleContextMenu)
    
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
    }
    
    // Restore QR if hidden
    if (this.qrHidden) {
      this.showQR()
    }
    
    console.log('[ScreenshotProtection] Protection disabled')
  }
  
  /**
   * Регистрирует callback для уведомления о попытке скриншота
   */
  static onScreenshotAttempt(callback: () => void): () => void {
    this.callbacks.add(callback)
    return () => this.callbacks.delete(callback)
  }
  
  /**
   * Уведомляет о попытке скриншота
   */
  private static notifyAttempt(): void {
    console.warn('[ScreenshotProtection] Screenshot attempt detected!')
    this.callbacks.forEach(cb => cb())
  }
  
  /**
   * Отправляет уведомление на сервер о попытке скриншота
   */
  static async reportScreenshotAttempt(employeeId: string): Promise<void> {
    try {
      // Collect device details for audit trail
      const deviceInfo = {
        employee_id: employeeId,
        timestamp: new Date().toISOString(),
        platform: navigator.platform,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        device_memory: (navigator as any).deviceMemory || 'unknown',
        hardware_concurrency: navigator.hardwareConcurrency || 'unknown',
        connection_type: (navigator as any).connection?.effectiveType || 'unknown',
        battery_level: await ScreenshotProtection.getBatteryLevel(),
      }
      
      await fetch(`${API_BASE_URL}/api/v1/worker/violations/screenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deviceInfo),
      })
    } catch (error) {
      console.error('[ScreenshotProtection] Failed to report screenshot attempt:', error)
    }
  }
  
  /**
   * Get battery level (if available)
   */
  private static async getBatteryLevel(): Promise<string> {
    try {
      if ('getBattery' in navigator) {
        const battery = await (navigator as any).getBattery()
        return `${Math.round(battery.level * 100)}%`
      }
    } catch {
      // Battery API not available
    }
    return 'unknown'
  }
  
  /**
   * Показывает предупреждение пользователю
   */
  static showWarning(): void {
    // Создаем overlay
    const overlay = document.createElement('div')
    overlay.id = 'screenshot-warning-overlay'
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.95);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
      animation: fadeIn 0.2s ease-out;
    `
    
    const content = document.createElement('div')
    content.style.cssText = `
      background: linear-gradient(145deg, #1a1a2e, #16213e);
      border-radius: 20px;
      padding: 2rem;
      max-width: 400px;
      text-align: center;
      color: white;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    `
    content.innerHTML = `
      <div style="font-size: 4rem; margin-bottom: 1rem;">⚠️</div>
      <h2 style="margin: 0 0 0.5rem 0; font-size: 1.5rem;">Скриншоты запрещены</h2>
      <p style="margin: 0 0 1.5rem 0; opacity: 0.7; font-size: 0.95rem;">
        Попытка сделать скриншот была зафиксирована и отправлена администратору.
      </p>
      <button id="screenshot-warning-close" style="
        padding: 0.75rem 2rem;
        background: #ef4444;
        color: white;
        border: none;
        border-radius: 10px;
        font-size: 1rem;
        cursor: pointer;
        font-weight: 500;
      ">Понятно</button>
    `
    
    overlay.appendChild(content)
    document.body.appendChild(overlay)
    
    // Закрытие по кнопке
    document.getElementById('screenshot-warning-close')?.addEventListener('click', () => {
      overlay.remove()
    })
    
    // Автозакрытие через 5 секунд
    setTimeout(() => {
      overlay.remove()
    }, 5000)
  }
  
  // === Event Handlers ===
  
  private static handleKeydown = (e: KeyboardEvent): void => {
    // PrintScreen
    if (e.key === 'PrintScreen') {
      e.preventDefault()
      ScreenshotProtection.notifyAttempt()
      return
    }
    
    // Windows: Win + Shift + S
    if (e.metaKey && e.shiftKey && e.key.toLowerCase() === 's') {
      e.preventDefault()
      ScreenshotProtection.notifyAttempt()
      return
    }
    
    // macOS: Cmd + Shift + 3/4/5
    if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
      e.preventDefault()
      ScreenshotProtection.notifyAttempt()
      return
    }
    
    // Alt + PrintScreen
    if (e.altKey && e.key === 'PrintScreen') {
      e.preventDefault()
      ScreenshotProtection.notifyAttempt()
      return
    }
  }
  
  private static handleKeyup = (e: KeyboardEvent): void => {
    if (e.key === 'PrintScreen') {
      // Очистка буфера обмена (попытка)
      if (navigator.clipboard) {
        navigator.clipboard.writeText('').catch(() => {})
      }
    }
  }
  
  /**
   * ГЛАВНЫЙ МЕТОД ОБНАРУЖЕНИЯ СКРИНШОТОВ
   * 
   * На iOS/Android при создании скриншота ЧАСТО (но не всегда) срабатывает:
   * - document.visibilityState меняется на 'hidden' на 50-200ms
   * - затем возвращается в 'visible'
   * 
   * Это происходит потому что система временно приостанавливает приложение
   * для создания скриншота.
   */
  private static handleVisibilityChange = (): void => {
    const now = Date.now()
    
    if (document.visibilityState === 'hidden') {
      ScreenshotProtection.lastVisibilityChange = now
      ScreenshotProtection.visibilityHideCount++
      
      // ПРЯЧЕМ QR НЕМЕДЛЕННО при переходе в hidden
      // Это ключевой момент - если это скриншот, QR будет скрыт
      ScreenshotProtection.hideQR()
      
      console.log('[ScreenshotProtection] 🔒 Page hidden - QR concealed (possible screenshot)')
      
    } else if (document.visibilityState === 'visible') {
      const hideDuration = now - ScreenshotProtection.lastVisibilityChange
      
      // Если страница была hidden короткое время (50-500ms) - ОЧЕНЬ ПОДОЗРИТЕЛЬНО
      // Именно такой паттерн характерен для скриншота
      if (hideDuration > 0 && hideDuration < 500) {
        console.log(`[ScreenshotProtection] ⚠️ Quick visibility change (${hideDuration}ms) - HIGH probability of screenshot`)
        ScreenshotProtection.notifyAttempt()
        
        // Держим QR скрытым еще 200ms для гарантии
        setTimeout(() => {
          ScreenshotProtection.showQR()
        }, 200)
      } else if (hideDuration > 500 && hideDuration < 2000) {
        // Средняя задержка - возможно скриншот, возможно переключение приложений
        console.log(`[ScreenshotProtection] ⚠️ Medium visibility change (${hideDuration}ms) - possible screenshot`)
        ScreenshotProtection.notifyAttempt()
        ScreenshotProtection.showQR()
      } else {
        // Долгая задержка - скорее всего переключение приложений, не скриншот
        ScreenshotProtection.showQR()
      }
      
      // Reset counter if no rapid changes
      if (hideDuration > 2000) {
        ScreenshotProtection.visibilityHideCount = 0
      }
    }
  }
  
  /**
   * ПРЯЧЕМ QR - ключевой метод
   * Делает QR полностью невидимым и непригодным для скриншота
   */
  private static hideQR = (): void => {
    if (ScreenshotProtection.qrHidden) return
    
    const qrElements = document.querySelectorAll('.qr-display__code, .qr-display__image')
    qrElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        // Полностью прячем - быстро и эффективно
        el.style.opacity = '0'
        el.style.visibility = 'hidden'
        el.style.pointerEvents = 'none'
      }
    })
    
    ScreenshotProtection.qrHidden = true
  }
  
  /**
   * ПОКАЗЫВАЕМ QR обратно
   */
  private static showQR = (): void => {
    if (!ScreenshotProtection.qrHidden) return
    
    const qrElements = document.querySelectorAll('.qr-display__code, .qr-display__image')
    qrElements.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.opacity = ''
        el.style.visibility = ''
        el.style.pointerEvents = ''
      }
    })
    
    ScreenshotProtection.qrHidden = false
  }
  
  private static handleContextMenu = (e: Event): void => {
    e.preventDefault()
  }
}

